// Iteration 140 -- Coach Inbox (two-way parent<->coach threads).
// One page for both roles: a conversation list + the selected thread + reply box.
// Coaches triage "awaiting reply"; parents continue threads they started.

import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type {
  ConversationSummary,
  ConversationDetailResponse,
} from "@dugads/shared";
import * as api from "@/lib/api";
import { MessageSquare, Send, CornerUpLeft, ChevronLeft } from "lucide-react";
import { useMediaQuery } from "@/lib/use-media-query";

const fmt = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

const AVATAR_COLORS = ["bg-blue-600", "bg-rose-600", "bg-green-600", "bg-violet-600", "bg-orange-600", "bg-teal-600"];
function avatarColor(s: string): string {
  let h = 0;
  for (const ch of s) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}
function nameInitials(s: string): string {
  return s.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() || "?";
}

export default function InboxPage() {
  const isMdUp = useMediaQuery("(min-width: 768px)");
  const [params, setParams] = useSearchParams();
  const [list, setList] = useState<ConversationSummary[] | null>(null);
  const [active, setActive] = useState<ConversationDetailResponse | null>(null);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);

  const selectedId = params.get("c");

  const loadList = useCallback(async () => {
    try {
      const res = await api.getMyConversations();
      setList(res.conversations);
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to load inbox");
      setList([]);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openThread = useCallback(async (id: string) => {
    setParams((p) => { p.set("c", id); return p; }, { replace: true });
    try {
      const detail = await api.getConversation(id);
      setActive(detail);
      // opening marks read -> refresh the list's unread flags
      loadList();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to open conversation");
    }
  }, [setParams, loadList]);

  // Open the thread named in the URL once the list is available.
  useEffect(() => {
    if (selectedId && (!active || active.id !== selectedId)) {
      openThread(selectedId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to the URL id
  }, [selectedId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [active?.messages.length]);

  async function submitReply() {
    if (!active || !reply.trim() || sending) return;
    setSending(true);
    try {
      await api.sendConversationMessage(active.id, { body: reply.trim() });
      setReply("");
      const detail = await api.getConversation(active.id);
      setActive(detail);
      loadList();
    } catch (e) {
      setError(e instanceof api.ApiError ? e.message : "Failed to send");
    } finally {
      setSending(false);
    }
  }

  function closeThread() {
    setParams((p) => { p.delete("c"); return p; }, { replace: true });
    setActive(null);
  }

  // Mobile: one pane at a time -- the conversation list, or (when one is open)
  // the full-screen thread with a sticky reply box.
  if (!isMdUp) {
    if (selectedId && active) {
      return (
        <div className="-m-4 flex h-[calc(100dvh-7.5rem)] flex-col bg-background" data-page="inbox" data-view="thread">
          <div className="flex items-center gap-1 border-b border-border px-2 py-2">
            <button type="button" onClick={closeThread} aria-label="Back" className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground active:bg-muted">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">{active.counterpart}</p>
              <p className="truncate text-[11px] text-muted-foreground">
                {active.team_name} &middot; {active.player_name}
              </p>
            </div>
          </div>
          <div className="flex flex-1 flex-col gap-2 overflow-y-auto px-3 py-3" data-region="thread">
            {active.messages.map((m) => {
              const mine = m.sender_role === active.my_role;
              return (
                <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-message={m.id}>
                  <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "rounded-br-md bg-primary text-primary-foreground" : "rounded-bl-md border border-border bg-card text-foreground"}`}>
                    {m.body}
                  </div>
                  <span className="mt-0.5 px-1 text-[10px] text-muted-foreground">{fmt(m.created_at)}</span>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          {error && <p className="px-3 text-sm text-destructive" data-error>{error}</p>}
          <div className="flex items-center gap-2 border-t border-border bg-background p-2 pb-[env(safe-area-inset-bottom)]">
            <input
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submitReply(); } }}
              placeholder="Message"
              className="h-10 flex-1 rounded-full border border-input bg-background px-4 text-sm focus:border-primary focus:outline-none"
            />
            <button
              type="button"
              data-action="send-reply"
              onClick={submitReply}
              disabled={sending || !reply.trim()}
              aria-label="Send"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="flex flex-col gap-3" data-page="inbox" data-view="list">
        {error && <p className="text-sm text-destructive" data-error>{error}</p>}
        {list === null ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading...</p>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-border bg-card py-12 text-center shadow-sm">
            <MessageSquare className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No conversations yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            {list.map((c) => (
              <button
                key={c.id}
                type="button"
                data-conversation={c.id}
                onClick={() => openThread(c.id)}
                className="flex w-full items-center gap-3 px-3 py-3 text-left transition-colors active:bg-muted/50"
              >
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${avatarColor(c.counterpart)}`}>
                  {nameInitials(c.counterpart)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-sm font-semibold">{c.counterpart}</span>
                    <span className="shrink-0 text-[11px] text-muted-foreground">{fmt(c.last_message_at)}</span>
                  </div>
                  <p className="truncate text-xs text-muted-foreground">{c.last_message_preview}</p>
                </div>
                {c.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="unread" />}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-page="inbox">
      <div>
        <h1 className="text-2xl font-semibold">Inbox</h1>
        <p className="text-muted-foreground">Direct conversations between parents and coaches.</p>
      </div>
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[20rem_1fr]">
        {/* Conversation list */}
        <div className="flex flex-col gap-2 md:max-h-[calc(100vh-12rem)] md:overflow-y-auto" data-region="conversation-list">
          {list === null ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : list.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border py-10 text-center">
              <MessageSquare className="h-6 w-6 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">No conversations yet.</p>
            </div>
          ) : (
            list.map((c) => (
              <button
                key={c.id}
                type="button"
                data-conversation={c.id}
                onClick={() => openThread(c.id)}
                className={`flex flex-col gap-0.5 rounded-lg border px-3 py-2 text-left transition ${
                  c.id === active?.id ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
                }`}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">
                    {c.counterpart}
                    <span className="ml-1 font-normal text-muted-foreground">&middot; {c.player_name}</span>
                  </span>
                  {c.unread && <span className="h-2 w-2 shrink-0 rounded-full bg-primary" aria-label="unread" />}
                </span>
                <span className="truncate text-xs text-muted-foreground">{c.last_message_preview}</span>
                <span className="flex items-center gap-2 text-[11px] text-muted-foreground">
                  <span>{fmt(c.last_message_at)}</span>
                  {c.my_role === "coach" && c.awaiting_coach_reply && (
                    <span className="rounded-full bg-[#FAEEDA] px-2 py-0.5 font-medium text-[#633806]">Awaiting reply</span>
                  )}
                </span>
              </button>
            ))
          )}
        </div>

        {/* Active thread */}
        <div className="flex min-h-[24rem] flex-col rounded-lg border border-border" data-region="thread">
          {!active ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
              <CornerUpLeft className="h-6 w-6" />
              <p className="text-sm">Select a conversation.</p>
            </div>
          ) : (
            <>
              <div className="border-b border-border px-4 py-3">
                <p className="font-medium">{active.counterpart}</p>
                <p className="text-xs text-muted-foreground">
                  {active.team_name} &middot; {active.player_name}
                  {active.subject ? ` · ${active.subject}` : ""}
                </p>
              </div>
              <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3 md:max-h-[calc(100vh-22rem)]">
                {active.messages.map((m) => {
                  const mine = m.sender_role === active.my_role;
                  return (
                    <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-message={m.id}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                        {m.body}
                      </div>
                      <span className="mt-0.5 text-[11px] text-muted-foreground">
                        {m.sender_name} &middot; {fmt(m.created_at)}
                      </span>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="flex items-end gap-2 border-t border-border p-3">
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) submitReply(); }}
                  rows={2}
                  placeholder="Write a reply... (Ctrl+Enter to send)"
                  className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none"
                />
                <button
                  type="button"
                  data-action="send-reply"
                  onClick={submitReply}
                  disabled={sending || !reply.trim()}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-50"
                >
                  <Send className="h-4 w-4" aria-hidden /> Send
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
